import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { fetchAdminSetup, saveSetupProgress, setupQueryKey, type AdminSetup } from "./api";

/** Fresh provisioned tenants only; never intercept an existing user's deep link. */
export function AdminSetupLaunch(): React.ReactElement | null {
  const { capabilities } = useEffectivePermissions();
  const location = useLocation();
  const client = useQueryClient();
  const attempted = useRef(false);
  const [launchLocationKey, setLaunchLocationKey] = useState<string | null>(null);
  const canEdit = capabilities.has("settings.edit");
  const eligiblePath = location.pathname === "/" || location.pathname === "/settings";
  const query = useQuery({ queryKey: setupQueryKey, queryFn: fetchAdminSetup,
    enabled: eligiblePath && capabilities.has("settings.read") && canEdit, staleTime: 60_000 });
  useEffect(() => {
    const setup = query.data;
    if (!eligiblePath || !canEdit || attempted.current || !setup?.progress.auto_launch || setup.progress.completed_at) return;
    attempted.current = true;
    void saveSetupProgress({ action: "visit", step_id: setup.progress.current_step, expected_revision: setup.progress.revision }, crypto.randomUUID())
      .then((progress) => {
        client.setQueryData<AdminSetup>(setupQueryKey, { ...setup, progress });
        setLaunchLocationKey(location.key);
      })
      // A failed launch does not prevent normal work. The admin home keeps the
      // persistent checklist available; no redirect/retry loop is created.
      .catch(() => { void client.invalidateQueries({ queryKey: setupQueryKey }); });
  }, [query.data, eligiblePath, canEdit, location.key, client]);
  return eligiblePath && canEdit && launchLocationKey === location.key ? <Navigate to="/settings/setup" replace /> : null;
}
